"use client";
import { useUser } from "@/components/clients/LocaleToStorage";
import Input, { type InputProps } from "@/components/ui/input";
import Title from "@/components/ui/title";
import { ValidateFormItem } from "@/components/ui/validate";
import { useI18n, useScopedI18n } from "@/messages/client";
import { useCurrentLocale } from "@/hooks/useCurrentLocale";
import { Mail, Smartphone } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { CitySelectForm } from "../UserLayout/Register";
import FormLocation from "../Form/FormLocation";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useSearchParams } from "next/navigation";
import FormApplication from "../Form/FormApplication";
import FormSelect from "../Form/FormSelect";
import Textarea from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useCodeMessage } from "@/hooks/useCodeMessage";
import toast from "react-hot-toast";
import useSWRMutation from "swr/mutation";
import useCountDown from "@/hooks/useCountDown";
import {
  getContactFormId,
  postFormApply,
  // postFormContent,
  sendEmailCode,
  // verifyEmailCode,
} from "@/server/form";
import { useSite } from "@/hooks/useSiteInfo";
import { isInteger } from "lodash-es";
import { cn } from "@/lib/utils";
import FormPhoneAreaCode from "../Form/FormPhoneAreaCode";
import useLocaleRouter from "@/hooks/useLocaleRouter";

type QuestionType = "selection" | "application" | "quality" | "technical";

interface ConsultParams {
  name: string;
  phoneArea: string;
  phoneNumber: string;
  email: string;
  code: string;
  organization: string;
  position: string;
  location: string;
  locationName: string;
  city: string;
  cityName: string;
  materialNumber: string;
  application: string;
  applicationName: string;
  questionType: QuestionType;
  questionTypeName: string;
  purchaseChannel: string;
  annualUsage: string;
  terminalApplication: string;
  question: string;
}

type FormType = "technical_consult" | "sales_consult" | "other_consult";

const questionTypes: {
  value: QuestionType;
  name: string;
  name_en: string;
}[] = [
  {
    value: "selection",
    name: "产品选型问题",
    name_en: "Product selection",
  },
  {
    value: "application",
    name: "产品应用问题",
    name_en: "Application related",
  },
  {
    value: "quality",
    name: "质量相关问题",
    name_en: "Quality related",
  },
  {
    value: "technical",
    name: "其他技术问题",
    name_en: "Other technical question",
  },
];

export function CodeEmailInput(props: InputProps) {
  const t = useScopedI18n("注册模块");

  const locale = useCurrentLocale();

  const { control, trigger: formTrigger } = useFormContext<ConsultParams>();

  const email = useWatch({
    control,
    name: "email",
  });

  const { count, start, isLoading, clear } = useCountDown(60);

  const { isMutating, trigger } = useSWRMutation(
    email ? `/api/forms/code/${email}` : null,
    () => {
      if (!email) {
        return undefined;
      }

      return sendEmailCode({
        email,
        language: locale === "cn" ? "zh" : locale,
      });
    },
    {
      onSuccess: (res) => {
        if (res?.data === "success") {
          start();

          toast.success(t("验证码已发送至邮箱"));
        }
      },
      onError: () => {
        clear();
        toast.error(t("验证码发送失败"));
      },
    }
  );

  return (
    <Input
      {...props}
      placeholder={t("请输入验证码")}
      suffix={
        <Button
          variant="link"
          type="button"
          loading={isMutating}
          disabled={isLoading}
          className="cursor-pointer whitespace-nowrap pl-2 pr-0 text-xs text-[#2967E3] transition-opacity hover:opacity-80"
          onClick={() => {
            if (!email) {
              formTrigger("email");

              return;
            }

            trigger();
          }}>
          {isLoading
            ? t<any, any>("重新发送", {
                count: count ?? 0,
              })
            : t("获取验证码")}
        </Button>
      }
    />
  );
}

export default function BusinessConsult() {
  const t = useI18n();

  const site = useSite();

  const locale = useCurrentLocale();

  const router = useLocaleRouter();

  const searchParams = useSearchParams();

  const materialNumber = searchParams.get("material_number");
  const applicationCategory = searchParams.get("application_category");
  const form_type = searchParams.get("form_type") as FormType;

  const { user, token } = useUser();

  const codeMessage = useCodeMessage();

  const [formType, setFormType] = useState<FormType>("technical_consult");

  const [success, setSuccess] = useState(false);

  const methods = useForm<ConsultParams>();

  const { handleSubmit, control, setValue } = methods;

  const { trigger, isMutating } = useSWRMutation(
    ["/api/form/create", site, token, formType],
    async ([, site, token, formSlug], { arg }: { arg: ConsultParams }) => {
      const { code, ...formData } = arg;
      // const res = await verifyEmailCode({
      //   email: formData.email,
      //   code: code,
      // });
      // if (!res.data) {
      //   return {
      //     errors: [
      //       {
      //         message: t("表单.邮箱验证码错误"),
      //       },
      //     ],
      //   };
      // }
      const formId = await getContactFormId(site, formSlug);
      if (!isInteger(formId)) {
        return formId;
      }
      return postFormApply({
        token: token as string,
        formId: formId as number,
        formData,
        code,
      });
    },
    {
      onError: () => {
        toast.error(t("表单.提交失败提示"));
      },
    }
  );

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (!token) {
        return;
      }
      const res = await trigger(data);

      if ("errors" in res) {
        toast.error(
          codeMessage(res.errors[0].extensions?.code, res.errors[0].message)
        );

        return;
      }

      toast.success(t("业务咨询.提交成功提示"), {
        duration: 3000,
      });
      setTimeout(() => {
        router.push("/");
      }, 3000);
      setSuccess(true);
    } catch (error) {
      toast.error(t("表单.提交失败提示"));
    }
  });

  const country = useWatch({
    control,
    name: "location",
  });

  const questionType = useWatch({
    control,
    name: "questionType",
  });

  const [renderDefaultCity, setRenderDefaultCity] = useState(false);

  useEffect(() => {
    if (user) {
      setValue("name", user.first_name);
      setValue("phoneArea", user.phone_area || "86");
      setValue("phoneNumber", user.phone_number);
      setValue("email", user.front_user_email);
      setValue("organization", user.organization);
      setValue("position", user.position);
      setValue("location", user.location);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user && country === "1" && !renderDefaultCity) {
      setTimeout(() => {
        setValue("city", user.city);
        setRenderDefaultCity(true);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, country, renderDefaultCity]);

  useEffect(() => {
    if (materialNumber) {
      setValue("materialNumber", materialNumber);
    }

    if (applicationCategory) {
      console.log(applicationCategory, "applicationCategory");
      setValue("application", applicationCategory);
    }

    if (form_type) {
      setFormType(form_type);
    }
  }, [applicationCategory, form_type, materialNumber, searchParams, setValue]);

  useEffect(() => {
    const questoinLabel = questionTypes.find((el) => el.value === questionType);
    setValue(
      "questionTypeName",
      (locale === "cn" ? questoinLabel?.name : questoinLabel?.name_en) ?? ""
    );

    if (questionType !== "quality") {
      setValue("purchaseChannel", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionType, locale]);

  return (
    <div className="w-full bg-white md:rounded-2xl md:px-44 md:pb-[85px] md:pt-[110px] md:shadow-lg">
      <FormProvider {...methods}>
        <form className="flex w-full flex-col" onSubmit={onSubmit}>
          <Title title={t("业务咨询.您的联系方式")} underline={false} />
          <span className="mt-4 text-sm text-[#CCC] md:mt-6 md:text-base">
            <span className="text-primary">*</span>
            {t("表单.为必填项")}
          </span>
          <div className="mt-5 grid grid-cols-1 gap-y-4 md:mt-8 md:grid-cols-2 md:gap-x-20 md:gap-y-12">
            <Controller
              name="name"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("注册模块.请输入姓名"),
                },
              }}
              render={({ field }) => (
                <ValidateFormItem name="name">
                  <Input
                    {...field}
                    required
                    suffix={<img src="/icon/user.svg" className="h-[18px]" />}
                    placeholder={t("注册模块.姓名")}
                  />
                </ValidateFormItem>
              )}
            />

            <Controller
              name="phoneNumber"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("注册模块.请输入手机号"),
                },
                pattern: {
                  value: /^[1-9]\d{1,14}$/,
                  message: t("注册模块.请输入有效的手机号"),
                },
              }}
              render={({ field }) => (
                <ValidateFormItem name="phoneNumber">
                  <Input
                    {...field}
                    type="tel"
                    autoComplete="off"
                    placeholder={t("注册模块.手机号")}
                    suffix={<Smartphone color="#CCC" size={18} />}
                    prefixs={
                      <Controller
                        name="phoneArea"
                        control={control}
                        render={({ field }) => (
                          <FormPhoneAreaCode
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                    }
                  />
                </ValidateFormItem>
              )}
            />

            <Controller
              name="email"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("注册模块.请输入邮箱地址"),
                },
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: t("注册模块.请输入有效的邮箱地址"),
                },
              }}
              render={({ field }) => (
                <ValidateFormItem name="email">
                  <Input
                    {...field}
                    required
                    type="email"
                    placeholder={t("注册模块.邮箱地址")}
                    suffix={<Mail color="#CCC" size={18} />}
                  />
                </ValidateFormItem>
              )}
            />

            <Controller
              name="code"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("注册模块.请输入邮箱验证码"),
                },
              }}
              render={({ field }) => (
                <ValidateFormItem name="code">
                  <CodeEmailInput
                    value={field.value}
                    onChange={field.onChange}
                    required
                    placeholder={t("注册模块.邮箱验证码")}
                  />
                </ValidateFormItem>
              )}
            />

            <Controller
              name="organization"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("注册模块.请输入公司"),
                },
              }}
              render={({ field }) => (
                <ValidateFormItem name="organization">
                  <Input
                    {...field}
                    required
                    suffix={
                      <img src="/icon/company.svg" className="h-[18px]" />
                    }
                    placeholder={t("注册模块.公司")}
                  />
                </ValidateFormItem>
              )}
            />

            <Controller
              name="position"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("注册模块.请输入职位"),
                },
              }}
              render={({ field }) => (
                <ValidateFormItem name="position">
                  <Input
                    {...field}
                    required
                    suffix={
                      <img src="/icon/position.svg" className="h-[18px]" />
                    }
                    placeholder={t("注册模块.职位")}
                  />
                </ValidateFormItem>
              )}
            />

            <Controller
              name="location"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("表单.请选择国家"),
                },
              }}
              render={({ field }) => (
                <ValidateFormItem name="location">
                  <FormLocation
                    className="md:data-[placeholder]:text-[#E5E5E5]"
                    field={field}
                    isFetching
                    level="1"
                    placeholder={t("注册模块.国家")}
                    required
                    onLabelChange={(label) => {
                      setValue("locationName", label);
                      setValue("cityName", "");
                      setValue("city", "");
                    }}
                  />
                </ValidateFormItem>
              )}
            />

            {country === "1" && (
              <Controller
                name="city"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t("表单.请选择城市"),
                  },
                }}
                render={({ field }) => (
                  <ValidateFormItem name="city">
                    <CitySelectForm
                      className="md:data-[placeholder]:text-[#E5E5E5]"
                      field={field}
                      required
                    />
                  </ValidateFormItem>
                )}
              />
            )}
          </div>
          <Title
            className="mt-10 md:mt-[120px]"
            title={t("业务咨询.咨询服务")}
            underline={false}
          />
          <Tabs
            value={formType}
            onValueChange={(val: any) => setFormType(val)}
            className="mt-5 md:mt-6">
            <TabsList className="flex justify-between border-none md:justify-center md:gap-x-[90px]">
              <TabsTrigger
                className="relative cursor-pointer border-none px-0 pb-3 text-base leading-normal after:absolute after:bottom-0 after:left-1/2 after:h-0 after:w-0 after:-translate-x-1/2 after:bg-primary after:duration-300 hover:text-primary data-[state=active]:text-primary data-[state=active]:after:h-1 data-[state=active]:after:w-full md:text-lg"
                value="technical_consult">
                {t("业务咨询.技术咨询")}
              </TabsTrigger>
              <TabsTrigger
                className="relative cursor-pointer border-none px-0 pb-3 text-base leading-normal after:absolute after:bottom-0 after:left-1/2 after:h-0 after:w-0 after:-translate-x-1/2 after:bg-primary after:duration-300 hover:text-primary data-[state=active]:text-primary data-[state=active]:after:h-1 data-[state=active]:after:w-full md:text-lg"
                value="sales_consult">
                {t("业务咨询.销售咨询")}
              </TabsTrigger>
              <TabsTrigger
                className="relative cursor-pointer border-none px-0 pb-3 text-base leading-normal after:absolute after:bottom-0 after:left-1/2 after:h-0 after:w-0 after:-translate-x-1/2 after:bg-primary after:duration-300 hover:text-primary data-[state=active]:text-primary data-[state=active]:after:h-1 data-[state=active]:after:w-full md:text-lg"
                value="other_consult">
                {t("业务咨询.其他咨询")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="technical_consult">
              <div className="mt-7 grid grid-cols-1 gap-y-4 md:mt-12 md:grid-cols-2 md:gap-x-20 md:gap-y-12">
                <Controller
                  name="materialNumber"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t("业务咨询.请输入产品料号"),
                    },
                  }}
                  render={({ field }) => (
                    <ValidateFormItem name="materialNumber">
                      <Input
                        {...field}
                        required
                        suffix={
                          <img
                            src="/icon/material_number.svg"
                            className="h-[18px]"
                          />
                        }
                        placeholder={t("业务咨询.产品料号")}
                      />
                    </ValidateFormItem>
                  )}
                />

                <Controller
                  name="application"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t("业务咨询.请选择应用场景"),
                    },
                  }}
                  render={({ field }) => (
                    <ValidateFormItem name="application">
                      <FormApplication
                        className="md:data-[placeholder]:text-[#E5E5E5]"
                        field={field}
                        isFetching
                        placeholder={t("业务咨询.应用场景")}
                        required
                        onLabelChange={(label) => {
                          setValue("applicationName", label);
                        }}
                      />
                    </ValidateFormItem>
                  )}
                />

                <div
                  className={cn({
                    "md:col-span-2": questionType !== "quality",
                  })}>
                  <Controller
                    name="questionType"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t("业务咨询.请选择您要咨询的问题类别"),
                      },
                    }}
                    render={({ field }) => (
                      <ValidateFormItem name="questionType">
                        <FormSelect
                          className="md:data-[placeholder]:text-[#E5E5E5]"
                          field={field}
                          placeholder={t("业务咨询.请选择您要咨询的问题类别")}
                          required
                          options={questionTypes.map((el) => ({
                            value: el.value,
                            label: locale === "cn" ? el.name : el.name_en,
                          }))}
                        />
                      </ValidateFormItem>
                    )}
                  />
                </div>

                {questionType === "quality" && (
                  <Controller
                    name="purchaseChannel"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t("业务咨询.请输入采购渠道"),
                      },
                    }}
                    render={({ field }) => (
                      <ValidateFormItem name="purchaseChannel">
                        <Input
                          {...field}
                          required
                          suffix={
                            <img
                              src="/icon/purchase.svg"
                              className="h-[18px]"
                            />
                          }
                          placeholder={t("业务咨询.采购渠道")}
                        />
                      </ValidateFormItem>
                    )}
                  />
                )}

                <div className="md:col-span-2">
                  <Controller
                    name="question"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t("业务咨询.请输入问题描述"),
                      },
                    }}
                    render={({ field }) => (
                      <ValidateFormItem name="question">
                        <Textarea
                          {...field}
                          className="h-36 py-3 md:h-64"
                          required
                          placeholder={
                            questionType === "quality"
                              ? t("业务咨询.请详细描述销售问题")
                              : t("业务咨询.请详细描述技术问题")
                          }
                        />
                      </ValidateFormItem>
                    )}
                  />
                </div>
              </div>
            </TabsContent>
            <TabsContent value="sales_consult">
              <div className="mt-7 grid grid-cols-1 gap-y-4 md:mt-12 md:grid-cols-2 md:gap-x-20 md:gap-y-12">
                <Controller
                  name="materialNumber"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t("业务咨询.请输入产品料号"),
                    },
                  }}
                  render={({ field }) => (
                    <ValidateFormItem name="materialNumber">
                      <Input
                        {...field}
                        required
                        suffix={
                          <img
                            src="/icon/material_number.svg"
                            className="h-[18px]"
                          />
                        }
                        placeholder={t("业务咨询.产品料号")}
                      />
                    </ValidateFormItem>
                  )}
                />

                <Controller
                  name="application"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t("业务咨询.请选择应用场景"),
                    },
                  }}
                  render={({ field }) => (
                    <ValidateFormItem name="application">
                      <FormApplication
                        className="md:data-[placeholder]:text-[#E5E5E5]"
                        field={field}
                        isFetching
                        placeholder={t("业务咨询.应用场景")}
                        required
                        onLabelChange={(label) => {
                          setValue("applicationName", label);
                        }}
                      />
                    </ValidateFormItem>
                  )}
                />

                <Controller
                  name="annualUsage"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t("业务咨询.请输入您的预计年用量"),
                    },
                  }}
                  render={({ field }) => (
                    <ValidateFormItem name="annualUsage">
                      <Input
                        {...field}
                        required
                        suffix={
                          <img src="/icon/usage.svg" className="h-[18px]" />
                        }
                        placeholder={t("业务咨询.请输入您的预计年用量")}
                      />
                    </ValidateFormItem>
                  )}
                />

                <Controller
                  name="terminalApplication"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t("业务咨询.您的在研产品的终端应用"),
                    },
                  }}
                  render={({ field }) => (
                    <ValidateFormItem name="terminalApplication">
                      <Input
                        {...field}
                        required
                        suffix={
                          <img src="/icon/ternimal.svg" className="h-[18px]" />
                        }
                        placeholder={t("业务咨询.请输入您的在研产品的终端应用")}
                      />
                    </ValidateFormItem>
                  )}
                />

                <div className="md:col-span-2">
                  <Controller
                    name="question"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t("业务咨询.请输入问题描述"),
                      },
                    }}
                    render={({ field }) => (
                      <ValidateFormItem name="question">
                        <Textarea
                          {...field}
                          className="h-36 py-3 md:h-64"
                          required
                          placeholder={t("业务咨询.请详细描述销售问题")}
                        />
                      </ValidateFormItem>
                    )}
                  />
                </div>
              </div>
            </TabsContent>
            <TabsContent value="other_consult">
              <div className="mt-7 grid grid-cols-1 gap-y-4 md:mt-12 md:grid-cols-2 md:gap-x-20 md:gap-y-12">
                <Controller
                  name="materialNumber"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t("业务咨询.请输入产品料号"),
                    },
                  }}
                  render={({ field }) => (
                    <ValidateFormItem name="materialNumber">
                      <Input
                        {...field}
                        required
                        suffix={
                          <img
                            src="/icon/material_number.svg"
                            className="h-[18px]"
                          />
                        }
                        placeholder={t("业务咨询.产品料号")}
                      />
                    </ValidateFormItem>
                  )}
                />

                <Controller
                  name="application"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t("业务咨询.请选择应用场景"),
                    },
                  }}
                  render={({ field }) => (
                    <ValidateFormItem name="application">
                      <FormApplication
                        className="md:data-[placeholder]:text-[#E5E5E5]"
                        field={field}
                        isFetching
                        placeholder={t("业务咨询.应用场景")}
                        required
                        onLabelChange={(label) => {
                          setValue("applicationName", label);
                        }}
                      />
                    </ValidateFormItem>
                  )}
                />

                <div className="md:col-span-2">
                  <Controller
                    name="question"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t("业务咨询.请输入问题描述"),
                      },
                    }}
                    render={({ field }) => (
                      <ValidateFormItem name="question">
                        <Textarea
                          {...field}
                          className="h-36 py-3 md:h-64"
                          required
                          placeholder={t("业务咨询.请详细描述问题")}
                        />
                      </ValidateFormItem>
                    )}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <Button
            type="submit"
            variant="outline"
            disabled={success}
            loading={isMutating}
            className="mt-10 self-center rounded-lg border-zinc-400 bg-zinc-100 max-md:w-full md:mt-24 md:min-w-[320px]">
            {t("表单.提交")}
          </Button>
        </form>
      </FormProvider>
    </div>
  );
}
